'use client'

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx'
import type { AppDispatch } from '@/store'
import styles from './styles.module.scss'
import {
  selectMemberInfoCompanies,
  selectMemberInfoCategories,
  updateFollowing,
} from '@/store/member/slice'
import { changeNonMemberModalState } from '@/store/modal/slice'
import { MembersService } from '@/lib/api/services/members'
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys'
interface FollowProps {
  followText: string
  type: 'company' | 'category'
  item: any
  card?: boolean
  used?: string
  preview?: boolean
}

export default function Follow({
  followText,
  type,
  item,
  card = false,
  used = '',
  preview = false,
}: FollowProps) {
  const [localFollowing, setLocalFollowing] = useState(false)
  const [isToggling, setIsToggling] = useState(false)
  const isTogglingRef = useRef(false)
  const followingCompanies = useSelector(selectMemberInfoCompanies)
  const followingCategories = useSelector(selectMemberInfoCategories)
  const dispatch = useDispatch<AppDispatch>()

  // typeに応じたフォロー配列を選択（favoriteStoryIds変更時の不要な再実行を防止）
  const targetArray = type === 'company' ? followingCompanies : followingCategories
  const storeBasedFollowing = useMemo(
    () => targetArray.some((target) => String(target.id) === String(item.id)),
    [targetArray, item.id],
  )

  const getMemberId = (): string | null => {
    if (typeof window === 'undefined') return null
    try {
      const memberData = sessionStorage.getItem(SESSION_STORAGE_KEYS.MEMBER)
      if (!memberData) return null

      const parsed = JSON.parse(memberData)
      if (parsed && typeof parsed === 'object' && parsed.id) {
        return String(parsed.id)
      }
      return null
    } catch {
      // JSONパースエラーまたはsessionStorageアクセスエラー
      return null
    }
  }

  const followItem = async (
    memberId: string,
    itemType: 'company' | 'category',
    itemId: number,
  ): Promise<void> => {
    if (itemType === 'company') {
      await MembersService.followCompany(memberId, itemId)
    } else {
      await MembersService.followCategory(memberId, itemId)
    }
  }

  const unfollowItem = async (
    memberId: string,
    itemType: 'company' | 'category',
    itemId: number,
  ): Promise<void> => {
    if (itemType === 'company') {
      await MembersService.unfollowCompany(memberId, itemId)
    } else {
      await MembersService.unfollowCategory(memberId, itemId)
    }
  }

  // storeの値が変更されたらlocalFollowingも同期（トグル中は除く）
  useEffect(() => {
    if (preview || isTogglingRef.current) return
    setLocalFollowing(storeBasedFollowing)
  }, [storeBasedFollowing, preview])

  const toggleFollow = async (event: React.MouseEvent) => {
    event.stopPropagation()

    if (preview) {
      setLocalFollowing(!localFollowing)
      return
    }

    const memberId = getMemberId()
    if (!memberId) {
      dispatch(changeNonMemberModalState(true))
      return
    }

    // Prevent multiple clicks
    if (isToggling || isTogglingRef.current) return

    setIsToggling(true)
    isTogglingRef.current = true
    const wasFollowing = localFollowing
    setLocalFollowing(!wasFollowing)

    try {
      if (wasFollowing) {
        await unfollowItem(memberId, type, item.id)
        // Update Redux state
        dispatch(
          updateFollowing({
            item,
            type,
            flag: 'remove',
          }),
        )
      } else {
        await followItem(memberId, type, item.id)
        // Update Redux state
        dispatch(
          updateFollowing({
            item,
            type,
            flag: 'add',
          }),
        )
      }
    } catch (error) {
      // Revert on error
      setLocalFollowing(wasFollowing)
    } finally {
      // Add small delay before clearing toggling state to ensure Redux updates complete
      setTimeout(() => {
        setIsToggling(false)
        isTogglingRef.current = false
      }, 100)
    }
  }

  return (
    <div
      className={clsx(styles.followButtonWrapper, {
        [styles.cardButton]: card,
        [styles[used]]: !!used,
      })}
    >
      <span
        className={clsx(styles.followButton, {
          [styles.follow]: localFollowing,
          noEvents: preview,
        })}
        onClick={toggleFollow}
        style={{ opacity: isToggling ? 0.7 : 1 }}
      >
        {localFollowing ? 'フォロー中' : followText}
      </span>
    </div>
  )
}
